import React from "react";
import SectionCrudPage from "./SectionCrudPage";
import {
  CrmDateTimeInput,
  CrmFormGrid,
  CrmNumberInput,
  CrmSelectInput,
  CrmTextInput,
} from "../../components/crm/CrudFormControls";

const asText = (value) => String(value ?? "").trim();
const asNullableText = (value) => {
  const next = asText(value);
  return next === "" ? null : next;
};
const asNullable = (value) => (value === "" || value === undefined ? null : value);
const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};
const asNullableNumber = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  return asNumber(value);
};
const asDateTime = (value) => {
  const next = asText(value);
  if (next === "") return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(next)) return `${next.replace("T", " ")}:00`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(next)) return next.replace("T", " ");
  return next;
};
const asNullableDateTime = (value) => {
  const next = asText(value);
  if (next === "") return null;
  return asDateTime(next);
};

const selectSource = (name, endpoint, labelFields, valueField = "id") => ({
  name,
  optionSource: { endpoint, valueField, labelFields },
});

function ProductsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
      />
      <CrmSelectInput
        name="product_type"
        label="Product Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "physical_item", label: "Physical Item" },
          { value: "membership_plan", label: "Membership Plan" },
          { value: "class_pack", label: "Class Pack" },
          { value: "service", label: "Service" },
          { value: "fee", label: "Fee" },
        ]}
      />
      <CrmTextInput
        name="code"
        label="Code"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="description"
        label="Description"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="vat_rate_id"
        label="VAT Rate"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.vat_rate_id || []}
        nullable
      />
      <CrmSelectInput
        name="pricing_strategy"
        label="Pricing Strategy"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "fixed", label: "Fixed" },
          { value: "price_table", label: "Price Table" },
        ]}
      />
      <CrmSelectInput
        name="inventory_item_id"
        label="Inventory Item"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.inventory_item_id || []}
        nullable
      />
    </CrmFormGrid>
  );
}

function ProductPricesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="product_id"
        label="Product"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.product_id || []}
      />
      <CrmSelectInput
        name="channel"
        label="Channel"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "any", label: "Any" },
          { value: "pos", label: "POS" },
          { value: "online", label: "Online" },
          { value: "app", label: "App" },
        ]}
      />
      <CrmDateTimeInput
        name="valid_from"
        label="Valid From (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="valid_to"
        label="Valid To (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="price"
        label="Price"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="currency"
        label="Currency"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function PromotionsForm({ formData, setFormData, errors, clearFieldError }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="description"
        label="Description"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="valid_from"
        label="Valid From (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="valid_to"
        label="Valid To (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="priority"
        label="Priority"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="discount_type"
        label="Discount Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "percent", label: "Percent" },
          { value: "amount", label: "Amount" },
        ]}
      />
      <CrmNumberInput
        name="discount_value"
        label="Discount Value"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function PromotionProductsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="promotion_id"
        label="Promotion"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.promotion_id || []}
      />
      <CrmSelectInput
        name="product_id"
        label="Product"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.product_id || []}
      />
    </CrmFormGrid>
  );
}

const catalogResources = [
  {
    key: "products",
    label: "Products",
    endpoint: "/products",
    relatedTabs: [
      {
        key: "product-prices",
        label: "Product Prices",
        endpoint: "/product-prices",
        foreignKey: "product_id",
      },
      {
        key: "promotion-products",
        label: "Promotion Links",
        endpoint: "/promotion-products",
        foreignKey: "product_id",
      },
    ],
    selectSources: [
      selectSource("vat_rate_id", "/vat-rates", ["code", "name"]),
      selectSource("inventory_item_id", "/inventory-items", ["name", "sku", "uuid"]),
    ],
    renderForm: ProductsForm,
    toPayload: (formData) => ({
      status: asText(formData.status),
      product_type: asText(formData.product_type),
      code: asText(formData.code),
      name: asText(formData.name),
      description: asNullableText(formData.description),
      vat_rate_id: asNullable(formData.vat_rate_id),
      pricing_strategy: asText(formData.pricing_strategy),
      inventory_item_id: asNullable(formData.inventory_item_id),
    }),
  },
  {
    key: "product-prices",
    label: "Product Prices",
    endpoint: "/product-prices",
    tableFields: [
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      { name: "channel", label: "Channel" },
      { name: "valid_from", label: "Valid From" },
      { name: "valid_to", label: "Valid To" },
      { name: "price", label: "Price" },
      { name: "currency", label: "Currency" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("product_id", "/products", ["name", "code", "uuid"]),
    ],
    renderForm: ProductPricesForm,
    toPayload: (formData) => ({
      product_id: asNullable(formData.product_id),
      channel: asText(formData.channel),
      valid_from: asDateTime(formData.valid_from),
      valid_to: asNullableDateTime(formData.valid_to),
      price: asNumber(formData.price),
      currency: asText(formData.currency),
    }),
  },
  {
    key: "promotions",
    label: "Promotions",
    endpoint: "/promotions",
    relatedTabs: [
      {
        key: "promotion-products",
        label: "Promotion Products",
        endpoint: "/promotion-products",
        foreignKey: "promotion_id",
      },
    ],
    renderForm: PromotionsForm,
    toPayload: (formData) => ({
      status: asText(formData.status),
      name: asText(formData.name),
      description: asNullableText(formData.description),
      valid_from: asDateTime(formData.valid_from),
      valid_to: asNullableDateTime(formData.valid_to),
      priority: asNumber(formData.priority),
      discount_type: asText(formData.discount_type),
      discount_value: asNumber(formData.discount_value),
    }),
  },
  {
    key: "promotion-products",
    label: "Promotion Products",
    endpoint: "/promotion-products",
    tableFields: [
      {
        name: "promotion_id",
        label: "Promotion",
        valueGetter: (row) => row?.promotion_name || row?.promotion?.name || row?.promotion_id,
      },
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("promotion_id", "/promotions", ["name", "uuid"]),
      selectSource("product_id", "/products", ["name", "code", "uuid"]),
    ],
    renderForm: PromotionProductsForm,
    toPayload: (formData) => ({
      promotion_id: asNullable(formData.promotion_id),
      product_id: asNullable(formData.product_id),
    }),
  },
];

export default function CatalogPage() {
  return (
    <SectionCrudPage
      title="Sales Catalog"
      subtitle="Manage products, price tables and promotions."
      resources={catalogResources}
    />
  );
}
